









































import { SfImage, SfRadio } from '~/components/StorefrontUi';
import {
  ref, onMounted, defineComponent, computed, useContext,
} from '@nuxtjs/composition-api';
import type {
  PropType,
} from '@nuxtjs/composition-api';
import usePaymentProvider from '~/modules/checkout/composables/usePaymentProvider';
import useCart from '~/modules/checkout/composables/useCart';
import cartGetters from '~/modules/checkout/getters/cartGetters';
import type { AvailablePaymentMethod } from '~/modules/GraphQL/types';
import { getPaymentIconsByCode } from '~/helpers/getShippingIconByTitle';
import {
  getMethodCode, PayuMethodCodesEnum,
} from '~/helpers/payuMethods';
import { usePayuMethodsStore } from '~/modules/checkout/stores/payuMethods';

export default defineComponent({
  name: 'VsfPaymentProvider',
  components: {
    SfRadio,
    SfImage,
    SectionHeading: () => import(/* webpackPrefetch: true */ '~/modules/checkout/components/SectionHeading.vue'),
  },
  props: {
    shouldShowCashOnDelivery: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },
  emits: ['status'],
  setup(props, { emit }) {
    const paymentMethods = ref<AvailablePaymentMethod[]>([]);
    const { load, save } = usePaymentProvider();
    const { cart } = useCart();
    const { app, env, i18n } = useContext();
    const selectedPaymentMethodCode = ref<string | null>(null);
    const { payuMethods } = usePayuMethodsStore();

    onMounted(async () => {
      paymentMethods.value = await load();
      const paymentMethodFromCart = cartGetters.getSelectedPaymentMethod(cart.value);
      if (paymentMethodFromCart) {
        selectedPaymentMethodCode.value = getMethodCode(paymentMethodFromCart);
        emit('status', selectedPaymentMethodCode.value);
      }
    });

    const definePaymentMethods = async (method: AvailablePaymentMethod) => {
      const paymentMethod = {
        code: method.code,
      } as { code: string, payu_additional_data?: { base_return_url: string } };
      if (method.payu_additional_data) {
        paymentMethod.payu_additional_data = {
          ...method.payu_additional_data,
          base_return_url: `${env.VSF_STORE_URL}${app.localeRoute({ name: 'thank-you' }).fullPath}`,
        };
      }
      paymentMethods.value = await save({ paymentMethod });
      selectedPaymentMethodCode.value = getMethodCode(method);

      emit('status', selectedPaymentMethodCode.value);
    };

    const visiblePaymentMethods = computed(() => {
      const validMethods = [];

      const additionalDataMap = {
        [PayuMethodCodesEnum.PAYU_GATEWAY]: {
          payu_additional_data: {
            payu_method_type: 'PBL',
            payu_method: '',
          },
        },
        [PayuMethodCodesEnum.PAYU_GATEWAY_CARD]: {
          payu_additional_data: {
            payu_method_type: 'PBL',
            payu_method: '',
          },
        },
        [PayuMethodCodesEnum.PAYU_GATEWAY_BLIK]: {
          payu_additional_data: {
            payu_method_type: 'BLIK_AUTHORIZATION_CODE',
            payu_method: '',
          },
        },
      };

      const payuMethod = paymentMethods.value.find((method) => method.code === PayuMethodCodesEnum.PAYU_GATEWAY);
      if (payuMethod) {
        payuMethods.forEach((method) => {
          validMethods.push({
            ...payuMethod,
            title: i18n.t(method.name),
            images: [method.brandImageUrl] ?? [],
            payu_additional_data: {
              payu_method_type: 'PBL',
              payu_method: method.value,
            },
          });
        });
      }
      paymentMethods.value.forEach((method) => {
        validMethods.push({
          ...method,
          title: i18n.t(method.title),
          images: getPaymentIconsByCode(method.code),
          ...additionalDataMap[method.code],
        });
      });
      return props.shouldShowCashOnDelivery ? validMethods : validMethods.filter((method) => method.code !== 'cashondelivery');
    });

    return {
      getMethodCode,
      visiblePaymentMethods,
      paymentMethods,
      selectedPaymentMethodCode,
      definePaymentMethods,
      getPaymentIconsByCode,
    };
  },
});
